import { useCallback, useEffect, useMemo, useState } from "react";
import { useAsync } from "@/shared/hooks/HookBase";
import DashboardService from "../services/DashboardService";
import type { Dashboard, DashboardUpsert } from "../models/Dashboard";

export function useDashboard(workspaceId: string) {
  const service = useMemo(() => new DashboardService(), []);
  const { loading, error, execute } = useAsync();

  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const result = await execute(() => service.get(workspaceId));
    setDashboard(result ?? null);
    setLoaded(true);
  }, [execute, service, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (upsert: DashboardUpsert) => {
      const saved = await execute(() => service.upsert(workspaceId, upsert), {
        successMessage: "Dashboard guardado.",
      });
      if (saved) setDashboard(saved);
      return saved ?? null;
    },
    [execute, service, workspaceId],
  );

  const remove = useCallback(async () => {
    await execute(() => service.delete(workspaceId), {
      successMessage: "Dashboard eliminado.",
    });
    setDashboard(null);
  }, [execute, service, workspaceId]);

  return { dashboard, loaded, loading, error, save, remove, reload: load };
}
